import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { IsOptional, IsString, IsInt, Min, Max, IsEnum } from 'class-validator';
import { Type } from 'class-transformer';
import { BadRequestException } from '@nestjs/common';
import { isValidUuid } from '@sphere/shared/domain';

/**
 * Ordre de tri (ascendant ou descendant)
 */
export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

/**
 * Configuration par défaut de la pagination
 */
export const PAGINATION_DEFAULTS = {
  LIMIT: 25,
  MAX_LIMIT: 100,
  PAGE: 1,
} as const;

/**
 * DTO pour la pagination cursor-based (keyset pagination)
 */
export class CursorPaginationQueryDto {
  @ApiPropertyOptional({
    type: 'string',
    description: 'Cursor de pagination opaque (pour page suivante)',
  })
  @IsOptional()
  @IsString()
  cursor?: string;

  @ApiPropertyOptional({
    type: Number,
    description: "Nombre d'éléments à retourner",
    default: PAGINATION_DEFAULTS.LIMIT,
    minimum: 1,
    maximum: PAGINATION_DEFAULTS.MAX_LIMIT,
    example: 25,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(PAGINATION_DEFAULTS.MAX_LIMIT)
  limit?: number = PAGINATION_DEFAULTS.LIMIT;

  @ApiPropertyOptional({
    type: 'string',
    description: 'Champ de tri (obligatoire pour stabilité)',
    example: 'createdAt',
  })
  @IsOptional()
  @IsString()
  sort?: string;

  @ApiPropertyOptional({
    description: 'Ordre de tri',
    enum: SortOrder,
    default: SortOrder.DESC,
    example: 'desc',
  })
  @IsOptional()
  @IsEnum(SortOrder)
  order?: SortOrder = SortOrder.DESC;

  @ApiPropertyOptional({
    type: 'string',
    description: 'Recherche textuelle',
  })
  @IsOptional()
  @IsString()
  q?: string;
}

/**
 * Métadonnées de pagination pour cursor-based
 */
export class CursorPageInfo {
  @ApiProperty({ description: "Limite d'éléments par page", example: 25 })
  limit!: number;

  @ApiProperty({ description: 'Champ de tri utilisé', example: 'createdAt' })
  sort!: string;

  @ApiProperty({ description: 'Ordre de tri (asc/desc)', example: 'desc' })
  order!: string;

  @ApiPropertyOptional({
    description: 'Cursor actuel utilisé pour cette page',
    nullable: true,
  })
  cursor!: string | null;

  @ApiProperty({ description: 'Y a-t-il une page suivante ?', example: true })
  hasNextPage!: boolean;

  @ApiPropertyOptional({
    description: 'Cursor pour la page suivante',
    nullable: true,
  })
  nextCursor!: string | null;

  @ApiProperty({
    description: 'Y a-t-il une page précédente ?',
    example: false,
  })
  hasPreviousPage!: boolean;

  @ApiPropertyOptional({
    description:
      'Cursor pour la page précédente (premier élément de la page actuelle)',
    nullable: true,
  })
  prevCursor!: string | null;

  @ApiPropertyOptional({
    description:
      'Dernier cursor de la page actuelle (=nextCursor ou dernier élément)',
    nullable: true,
  })
  endCursor!: string | null;

  @ApiPropertyOptional({
    description: "Nombre total d'éléments",
    example: 250,
    nullable: true,
  })
  totalCount!: number | null;
}

/**
 * Réponse de pagination cursor-based
 */
export class CursorPaginationResponseDto<T> {
  @ApiProperty({ description: 'Liste des éléments', isArray: true })
  data: T[];

  @ApiProperty({
    description: 'Métadonnées de pagination',
    type: CursorPageInfo,
  })
  pageInfo: CursorPageInfo;

  constructor(data: T[], pageInfo: CursorPageInfo) {
    this.data = data;
    this.pageInfo = pageInfo;
  }
}

/**
 * Encode un cursor pagination
 * Format: base64({ lastId, lastValue })
 */
export function encodeCursor(lastId: string, lastValue?: any): string {
  const payload = { lastId, lastValue };
  return Buffer.from(JSON.stringify(payload)).toString('base64');
}

/**
 * Decode un cursor pagination
 * @throws BadRequestException si le cursor est invalide (HTTP 400)
 */
export function decodeCursor(cursor: string): {
  lastId: string;
  lastValue?: any;
} {
  try {
    const decoded = Buffer.from(cursor, 'base64').toString('utf-8');
    const parsed = JSON.parse(decoded);

    // Valider la structure
    if (!parsed || typeof parsed !== 'object' || !parsed.lastId) {
      throw new BadRequestException(
        'Invalid pagination cursor: malformed structure',
      );
    }

    // Valider que lastId est un UUID valide (format v4)
    if (!isValidUuid(parsed.lastId)) {
      throw new BadRequestException(
        'Invalid pagination cursor: lastId must be a valid UUID',
      );
    }

    return parsed;
  } catch (error: any) {
    // Si c'est déjà une BadRequestException, la relancer
    if (error instanceof BadRequestException) {
      throw error;
    }

    // Sinon, wrapper dans BadRequestException
    const message = error?.message || 'Unknown error';
    throw new BadRequestException(`Invalid pagination cursor: ${message}`);
  }
}
